// WlcDateOfBirthPicker
// --------------------
// Self-contained day / month / year picker for the WLC screener.
// Emits an ISO date string (YYYY-MM-DD) once all three parts are valid.
//
// Adapted from the RMS DateOfBirthPicker with:
//   - minAgeYears defaulting to 17 (not 18) for the WLC RTC flow
//   - WLC brand accent colour on focus rings
//   - Smart day validation: adjusts available days for the selected month/year
//   - Leap-year handling for February

import React, { useState, useEffect, useMemo } from 'react';
import { parseDobString, buildDobString } from '../../../utils/dobDateUtils';

const BRAND_ACCENT = '#e7c22f';

const isLeapYear = (y) => (y % 4 === 0 && y % 100 !== 0) || (y % 400 === 0);

const getDaysInMonth = (monthNum, yearNum = null) => {
    const dpm = { 1:31, 2:29, 3:31, 4:30, 5:31, 6:30, 7:31, 8:31, 9:30, 10:31, 11:30, 12:31 };
    let days = dpm[monthNum] || 31;
    if (monthNum === 2 && yearNum && !isLeapYear(yearNum)) days = 28;
    return days;
};

export default function WlcDateOfBirthPicker({ value, onChange, minAgeYears = 17, maxAgeYears = 120 }) {
    const [day, setDay] = useState('');
    const [month, setMonth] = useState('');
    const [year, setYear] = useState('');

    // Sync internal state with external value prop. Uses parseDobString
    // (pure string splitting) instead of the Date constructor to avoid
    // UTC/local-time mismatch.
    useEffect(() => {
        if (value) {
            const parts = parseDobString(value);
            if (parts) {
                setDay(parts.day);
                setMonth(parts.month);
                setYear(parts.year);
                return;
            }
        }
        setDay(''); setMonth(''); setYear('');
    }, [value]);

    const maxDays = useMemo(() => {
        if (!month) return 31;
        return getDaysInMonth(parseInt(month, 10), year ? parseInt(year, 10) : null);
    }, [month, year]);

    const days = useMemo(() =>
        Array.from({ length: maxDays }, (_, i) => ({ value: String(i + 1).padStart(2, '0'), label: String(i + 1) })),
    [maxDays]);

    const months = [
        { value: '01', label: 'January' },  { value: '02', label: 'February' },
        { value: '03', label: 'March' },     { value: '04', label: 'April' },
        { value: '05', label: 'May' },       { value: '06', label: 'June' },
        { value: '07', label: 'July' },      { value: '08', label: 'August' },
        { value: '09', label: 'September' }, { value: '10', label: 'October' },
        { value: '11', label: 'November' },  { value: '12', label: 'December' },
    ];

    const currentYear = new Date().getFullYear();
    const endYear = currentYear - Number(minAgeYears || 0);
    const startYear = currentYear - Number(maxAgeYears || 120) - 1;
    const years = useMemo(() =>
        Array.from({ length: Math.max(0, endYear - startYear + 1) }, (_, i) => {
            const y = endYear - i;
            return { value: String(y), label: String(y) };
        }),
    [endYear, startYear]);

    // Validate and emit the date. Uses buildDobString (UTC-safe validation)
    // instead of new Date(isoString) + local-time accessors.
    const updateDate = (d, m, y) => {
        if (d && m && y) {
            const ds = buildDobString(d, m, y);
            if (ds) {
                onChange?.(ds);
            }
        } else if (!d && !m && !y) {
            onChange?.('');
        }
    };

    const isDayValid = (dVal, mVal, yVal = null) => {
        if (!dVal || !mVal) return true;
        return parseInt(dVal, 10) <= getDaysInMonth(parseInt(mVal, 10), yVal ? parseInt(yVal, 10) : null);
    };

    const handleDayChange = (v) => { setDay(v); updateDate(v, month, year); };

    const handleMonthChange = (v) => {
        setMonth(v);
        if (v === '02' && day && parseInt(day, 10) === 29 && !year) return;
        if (day && !isDayValid(day, v, year)) { setDay(''); return; }
        updateDate(day, v, year);
    };

    const handleYearChange = (v) => {
        setYear(v);
        if (day && month && !isDayValid(day, month, v)) { setDay(''); return; }
        updateDate(day, month, v);
    };

    // Minimal themed select
    const Select = ({ value: val, onChange: onCh, options: opts, placeholder: ph }) => {
        const [open, setOpen] = useState(false);
        const [pos, setPos] = useState(null);
        const ref = React.useRef(null);
        const sel = opts.find(o => o.value === val);

        const openMenu = () => {
            const rect = ref.current?.getBoundingClientRect();
            if (rect) {
                const vh = window.innerHeight;
                const below = vh - rect.bottom - 16;
                const above = rect.top - 16;
                const up = below < 150 && above > below;
                const mh = Math.min(up ? above : below, vh * 0.4);
                setPos(up
                    ? { left: rect.left, bottom: vh - rect.top + 4, width: rect.width, maxHeight: mh, up: true }
                    : { left: rect.left, top: rect.bottom + 4, width: rect.width, maxHeight: mh, up: false }
                );
            }
            setOpen(true);
        };

        return (
            <div className="relative" ref={ref}>
                <button
                    type="button"
                    className="w-full p-2 border-2 border-gray-200 rounded-lg bg-white text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:border-transparent text-sm"
                    style={{ '--tw-ring-color': BRAND_ACCENT }}
                    onClick={() => (open ? setOpen(false) : openMenu())}
                >
                    <span className={sel ? 'text-gray-900' : 'text-gray-400'}>{sel ? sel.label : ph}</span>
                    <svg className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                    </svg>
                </button>
                {open && (
                    <>
                        <div className="fixed inset-0 z-[70]" onClick={() => setOpen(false)} />
                        <div
                            className="fixed z-[80] bg-white border border-gray-300 rounded-md shadow-lg overflow-auto"
                            style={{
                                left: pos?.left || 0,
                                ...(pos?.up ? { bottom: pos?.bottom || 0 } : { top: pos?.top || 0 }),
                                width: pos?.width || 'auto',
                                maxHeight: pos?.maxHeight || '60vh',
                            }}
                        >
                            {opts.map(o => (
                                <button
                                    key={o.value}
                                    type="button"
                                    className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 focus:outline-none"
                                    onClick={() => { onCh(o.value); setOpen(false); }}
                                >
                                    {o.label}
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2">
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Day</label>
                    <Select value={day} onChange={handleDayChange} options={days} placeholder="Day" />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Month</label>
                    <Select value={month} onChange={handleMonthChange} options={months} placeholder="Month" />
                </div>
                <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Year</label>
                    <Select value={year} onChange={handleYearChange} options={years} placeholder="Year" />
                </div>
            </div>
        </div>
    );
}
